import { toast } from 'sonner';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { useSupabaseAuth } from '../hooks/useSupabaseAuth';

export const ProfileContext = createContext();

export const useProfileContext = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfileContext must be used within a ProfileProvider');
  }
  return context;
};

export const ProfileProvider = ({ children }) => {
  const { user, loading: authLoading } = useSupabaseAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    if (authLoading) return;

    if (!user?.id) {
      setProfile(null);
      setLoading(false);
      return;
    }

    try {
      // Only set hard loading state if we don't have a profile for this user yet
      setLoading((prevLoading) => {
        if (profile && profile.id === user.id) {
          return false; // Background refresh, don't show loading
        }
        return true;
      });

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching profile:", error);
        setError(error);
      } else {
        setProfile(data);
      }
    } catch (err) {
      console.error("Error in fetchProfile:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, authLoading]); // Use user?.id to prevent refresh on token update


  // Realtime subscription for achievements
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('public:user_achievements')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_achievements',
          filter: `user_id=eq.${user.id}`
        },
        async (payload) => {
          console.log('Achievement unlocked:', payload);
          // Fetch the achievement name
          const { data } = await supabase
            .from('achievements')
            .select('name')
            .eq('id', payload.new.achievement_id)
            .single();

          if (data) {
            toast.success(`Achievement Unlocked: ${data.name} 🏆`);
          }

          // Refresh profile to get updated points
          fetchProfile();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, fetchProfile]); // Use user?.id to prevent resubscribe on token refresh

  // Initial fetch
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const updateProfile = async (updates) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();

      if (error) throw error;

      setProfile(data);
      return data;
    } catch (err) {
      console.error("Error updating profile:", err);
      throw err;
    }
  };

  return (
    <ProfileContext.Provider value={{
      profile,
      loading: loading || authLoading,
      error,
      updateProfile,
      refreshProfile: fetchProfile
    }}>
      {children}
    </ProfileContext.Provider>
  );
};
